import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import timeZoneData from 'dashboard/routes/dashboard/settings/inbox/helpers/timezones.json';

const DEFAULT_LOCALE = 'en-US';
import { REPORTING_DEFAULT_TIMEZONE } from 'dashboard/helper/reportingTimezone';

// Mesmo default do servidor — ver dashboard/helper/reportingTimezone.
const DEFAULT_TIMEZONE = REPORTING_DEFAULT_TIMEZONE;

const ACTIVE_SUPPORT_TIMEZONE_SUFFIX = /\s+\((?:GMT|UTC)[+\-−]\d{2}:\d{2}\)$/u;
const ACTIVE_SUPPORT_TIMEZONE_ALIASES = Object.fromEntries(
  Object.entries(timeZoneData).map(([label, timeZone]) => [
    label.replace(ACTIVE_SUPPORT_TIMEZONE_SUFFIX, ''),
    timeZone,
  ])
);

const validTimeZone = timeZone => {
  try {
    new Intl.DateTimeFormat(DEFAULT_LOCALE, { timeZone }).format();
    return true;
  } catch {
    return false;
  }
};

const normalizeTimezoneCandidate = value => {
  const rawTimeZone = String(value || '').trim();
  if (!rawTimeZone) return null;
  if (validTimeZone(rawTimeZone)) return rawTimeZone;

  const aliasedTimeZone = ACTIVE_SUPPORT_TIMEZONE_ALIASES[rawTimeZone];
  return aliasedTimeZone && validTimeZone(aliasedTimeZone)
    ? aliasedTimeZone
    : null;
};

export const normalizeReportingTimezone = (
  value,
  fallback = DEFAULT_TIMEZONE
) => {
  return (
    [value, fallback, DEFAULT_TIMEZONE]
      .map(normalizeTimezoneCandidate)
      .find(Boolean) || DEFAULT_TIMEZONE
  );
};

const getterValue = value => (typeof value === 'function' ? value() : value);

const populatedAccount = account =>
  account &&
  typeof account === 'object' &&
  !Array.isArray(account) &&
  Object.keys(account).length > 0;

const normalizedAccountId = value => {
  if (value === null || value === undefined) return null;

  const normalized = String(value).trim();
  return normalized || null;
};

const accountForCurrentTenant = (account, currentAccountId) => {
  if (!populatedAccount(account) || !currentAccountId) return null;

  return normalizedAccountId(account.id) === currentAccountId ? account : null;
};

const accountSources = store => {
  const getters = store?.getters || {};
  const currentAccountId = getterValue(getters.getCurrentAccountId);
  const accountId = normalizedAccountId(currentAccountId);
  const accountLookup = getters['accounts/getAccount'];
  const storedAccountCandidate =
    accountId && typeof accountLookup === 'function'
      ? accountLookup(currentAccountId)
      : null;
  const sessionAccountCandidate = getterValue(getters.getCurrentAccount);
  const storedAccount = accountForCurrentTenant(
    storedAccountCandidate,
    accountId
  );
  const sessionAccount = accountForCurrentTenant(
    sessionAccountCandidate,
    accountId
  );

  return { storedAccount, sessionAccount };
};

export const resolveCurrentAccount = store => {
  const { storedAccount, sessionAccount } = accountSources(store);

  if (populatedAccount(storedAccount)) return storedAccount;
  if (populatedAccount(sessionAccount)) return sessionAccount;
  return {};
};

export const resolveReportingTimezone = (
  store,
  fallback = DEFAULT_TIMEZONE
) => {
  const { storedAccount, sessionAccount } = accountSources(store);
  const timezone = [
    storedAccount?.reporting_timezone,
    storedAccount?.settings?.reporting_timezone,
    sessionAccount?.reporting_timezone,
    sessionAccount?.settings?.reporting_timezone,
  ]
    .map(normalizeTimezoneCandidate)
    .find(Boolean);

  return timezone || normalizeReportingTimezone(fallback);
};

export const normalizeLocale = locale =>
  String(locale || DEFAULT_LOCALE).replace(/_/g, '-');

const CURRENCY_SYMBOL_CODES = {
  $: 'USD',
  US$: 'USD',
  R$: 'BRL',
  '€': 'EUR',
  '£': 'GBP',
};

export const normalizeCurrencyCode = value => {
  const candidate =
    value && typeof value === 'object'
      ? value.code || value.iso_code || value.value
      : value;
  const normalized = String(candidate ?? '').trim();
  if (!normalized) return null;

  const symbolCode = CURRENCY_SYMBOL_CODES[normalized.toUpperCase()];
  if (symbolCode) return symbolCode;

  return /^[A-Z]{3}$/i.test(normalized) ? normalized.toUpperCase() : null;
};

export const resolveAccountCurrency = (store, fallback = 'BRL') => {
  const { storedAccount, sessionAccount } = accountSources(store);
  const currency = [storedAccount, sessionAccount]
    .filter(populatedAccount)
    .flatMap(account => [account.currency, account.settings?.currency])
    .map(normalizeCurrencyCode)
    .find(Boolean);

  return currency || normalizeCurrencyCode(fallback) || 'BRL';
};

const NUMBER_LOCALE_CACHE = new Map();
const BIDI_CONTROL_CHARACTERS =
  /[\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/gu;

const normalizeDigitGlyphs = (value, digitMap) =>
  Array.from(value)
    .map(character => digitMap[character] ?? character)
    .join('');

const numberLocaleData = locale => {
  const requestedLocale = normalizeLocale(locale);
  const cached = NUMBER_LOCALE_CACHE.get(requestedLocale);
  if (cached) return cached;

  let formatter;
  try {
    formatter = new Intl.NumberFormat(requestedLocale, { useGrouping: true });
  } catch {
    formatter = new Intl.NumberFormat(DEFAULT_LOCALE, { useGrouping: true });
  }

  const digitMap = Object.fromEntries(
    Array.from({ length: 10 }, (_, digit) => {
      const glyph = formatter
        .formatToParts(digit)
        .filter(part => part.type === 'integer')
        .map(part => part.value)
        .join('');
      return [glyph, String(digit)];
    })
  );
  const parts = formatter.formatToParts(123456789012345.6);
  const integerGroupSizes = parts
    .filter(part => part.type === 'integer')
    .map(part => normalizeDigitGlyphs(part.value, digitMap).length);
  const primaryGroupSize = integerGroupSizes.at(-1) || 3;
  const secondaryGroupSize = integerGroupSizes.at(-2) || primaryGroupSize;
  const data = {
    locale: formatter.resolvedOptions().locale,
    decimal: parts.find(part => part.type === 'decimal')?.value || '.',
    group: parts.find(part => part.type === 'group')?.value || ',',
    minusSign:
      formatter.formatToParts(-1).find(part => part.type === 'minusSign')
        ?.value || '-',
    primaryGroupSize,
    secondaryGroupSize,
    digitMap,
  };

  NUMBER_LOCALE_CACHE.set(requestedLocale, data);
  return data;
};

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const currencyAffix = ({ locale, currency, currencySymbol }) => {
  const currencyCode = normalizeCurrencyCode(currency);
  if (!currencyCode && !currencySymbol) return null;

  let position = 'prefix';
  let minusPosition = 'before_currency';
  const tokens = [];
  if (currencyCode) {
    try {
      const parts = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
      }).formatToParts(-1234.5);
      const currencyIndex = parts.findIndex(part => part.type === 'currency');
      const integerIndex = parts.findIndex(part => part.type === 'integer');
      const minusIndex = parts.findIndex(part => part.type === 'minusSign');
      const token = parts[currencyIndex]?.value
        ?.replace(BIDI_CONTROL_CHARACTERS, '')
        .trim();
      if (token) tokens.push(token);
      if (currencyIndex > integerIndex) position = 'suffix';
      if (currencyIndex < minusIndex && minusIndex < integerIndex) {
        minusPosition = 'after_currency';
      }
    } catch {
      // A malformed currency code is handled by the residue validation below.
    }
  }

  const customToken = String(currencySymbol || '')
    .replace(BIDI_CONTROL_CHARACTERS, '')
    .trim();
  if (customToken) tokens.push(customToken);
  const uniqueTokens = [...new Set(tokens)].sort(
    (first, second) => second.length - first.length
  );
  if (!uniqueTokens.length) return null;

  return { minusPosition, position, tokens: uniqueTokens };
};

export const parseLocalizedNumber = (
  value,
  locale = DEFAULT_LOCALE,
  { currency = null, currencySymbol = '' } = {}
) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const input = String(value ?? '').trim();
  if (!input) return null;

  const {
    locale: resolvedLocale,
    decimal,
    group,
    minusSign,
    primaryGroupSize,
    secondaryGroupSize,
    digitMap,
  } = numberLocaleData(locale);
  const allowedSeparators = `${escapeRegExp(decimal)}${escapeRegExp(group)}`;
  const expectedCurrencyAffix = currencyAffix({
    locale: resolvedLocale,
    currency,
    currencySymbol,
  });
  const currencyPattern = expectedCurrencyAffix?.tokens
    .map(escapeRegExp)
    .join('|');
  const leadingCurrencyAffix = currencyPattern
    ? new RegExp(`^(?:${currencyPattern})\\s*`, 'iu')
    : /^(?:[A-Z]{1,3})?\p{Sc}\s*/iu;
  const trailingCurrencyAffix = currencyPattern
    ? new RegExp(`\\s*(?:${currencyPattern})$`, 'iu')
    : /\s*(?:[A-Z]{1,3})?\p{Sc}$/iu;
  let numericInput = input.replace(BIDI_CONTROL_CHARACTERS, '');
  const leadingMinusSign =
    minusSign === '-'
      ? /^-/
      : new RegExp(`^(?:-|${escapeRegExp(minusSign)})`, 'u');
  const hasLeadingMinusSign = leadingMinusSign.test(numericInput);
  if (hasLeadingMinusSign) {
    numericInput = numericInput.replace(leadingMinusSign, '');
  }
  const hasLeadingCurrency =
    expectedCurrencyAffix?.position === 'suffix'
      ? false
      : leadingCurrencyAffix.test(numericInput);
  const hasTrailingCurrency =
    expectedCurrencyAffix?.position === 'prefix'
      ? false
      : trailingCurrencyAffix.test(numericInput);
  if (hasLeadingCurrency && hasTrailingCurrency) return null;
  if (
    hasLeadingMinusSign &&
    hasLeadingCurrency &&
    expectedCurrencyAffix?.minusPosition === 'after_currency'
  ) {
    return null;
  }

  if (hasLeadingCurrency) {
    numericInput = numericInput.replace(leadingCurrencyAffix, '');
  } else if (hasTrailingCurrency) {
    numericInput = numericInput.replace(trailingCurrencyAffix, '');
  }
  if (/\s/u.test(group)) numericInput = numericInput.replace(/\s+/gu, group);
  if (minusSign !== '-') numericInput = numericInput.replaceAll(minusSign, '-');
  const hasCurrencyAdjacentMinus =
    !hasLeadingMinusSign &&
    hasLeadingCurrency &&
    expectedCurrencyAffix?.minusPosition === 'after_currency' &&
    numericInput.startsWith('-');
  if (hasCurrencyAdjacentMinus) {
    numericInput = numericInput.slice(1);
  }
  const hasNegativeSign = hasLeadingMinusSign || hasCurrencyAdjacentMinus;
  if (numericInput.includes('-')) return null;
  if (hasNegativeSign) {
    numericInput = `-${numericInput}`;
  }
  numericInput = normalizeDigitGlyphs(numericInput, digitMap);

  if (new RegExp(`[^\\d${allowedSeparators}-]`).test(numericInput)) {
    return null;
  }
  const decimalParts = numericInput.split(decimal);
  if (decimalParts.length > 2) return null;

  const [signedInteger, fraction] = decimalParts;
  const isNegative = signedInteger.startsWith('-');
  const integer = isNegative ? signedInteger.slice(1) : signedInteger;
  const groups = integer.split(group);
  const hasValidGrouping =
    groups.length === 1 ||
    (groups[0].length >= 1 &&
      groups[0].length <= secondaryGroupSize &&
      groups[groups.length - 1].length === primaryGroupSize &&
      groups.slice(1, -1).every(part => part.length === secondaryGroupSize));

  if (!hasValidGrouping || !groups.every(part => /^\d+$/.test(part))) {
    return null;
  }
  if (fraction !== undefined && !/^\d+$/.test(fraction)) return null;

  const normalized = `${isNegative ? '-' : ''}${groups.join('')}${
    fraction === undefined ? '' : `.${fraction}`
  }`;

  if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) return null;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export const formatLocalizedCurrency = (
  value,
  { currency = 'BRL', locale = DEFAULT_LOCALE } = {}
) => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return '';
  }

  const number = Number(value);
  if (!Number.isFinite(number)) return '';

  return new Intl.NumberFormat(normalizeLocale(locale), {
    style: 'currency',
    currency,
  }).format(number);
};

/**
 * Formata um número para EDIÇÃO num campo de texto — sem símbolo de moeda,
 * usando os separadores do locale. É o inverso de `parseLocalizedNumber`: o que
 * sai daqui volta a ser o mesmo número ao ser reenviado.
 *
 * Serve para reapresentar um valor vindo do backend ("2450.0") no formato que o
 * operador digitaria ("2.450,00"), em vez de mostrar o decimal cru num campo
 * cujo placeholder é brasileiro.
 *
 * @param {number|string|null} value
 * @param {string} locale
 * @returns {string} string vazia quando não há número
 */
export const formatEditableNumber = (value, locale = DEFAULT_LOCALE) => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return '';
  }

  const number = Number(value);
  if (!Number.isFinite(number)) return '';

  return new Intl.NumberFormat(normalizeLocale(locale), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

/**
 * Formata um número plano (contagem, percentual, dias) para EXIBIÇÃO com os
 * separadores do locale — sem símbolo de moeda. Parceiro somente-leitura de
 * `formatEditableNumber`: aquele sempre trava em 2 casas (é o formato de
 * campo de edição); este preserva as casas que o chamador pedir, para não
 * inventar precisão que o backend não mandou (ex.: `average_sales_cycle` já
 * chega arredondado a 1 casa — "1.2" deve virar "1,2", não "1,20").
 *
 * @param {number|string|null} value
 * @param {object} [options]
 * @param {string} [options.locale]
 * @param {number} [options.minimumFractionDigits]
 * @param {number} [options.maximumFractionDigits]
 * @returns {string} string vazia quando não há número
 */
export const formatLocalizedNumber = (
  value,
  { locale = DEFAULT_LOCALE, minimumFractionDigits, maximumFractionDigits } = {}
) => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return '';
  }

  const number = Number(value);
  if (!Number.isFinite(number)) return '';

  return new Intl.NumberFormat(normalizeLocale(locale), {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(number);
};

const missingDateValue = value =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '');

export const formatInReportingTimezone = (
  value,
  pattern,
  reportingTimezone = DEFAULT_TIMEZONE
) => {
  if (missingDateValue(value)) return '';

  try {
    return formatInTimeZone(
      value,
      normalizeReportingTimezone(reportingTimezone),
      pattern
    );
  } catch {
    return '';
  }
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const LOCAL_DATE_TIME_PATTERN =
  /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/;
const OFFSET_DATE_TIME_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(Z|[+-]\d{2}:?\d{2})$/i;

const validCalendarDateTimeParts = ([
  ,
  year,
  month,
  day,
  hour,
  minute,
  second = '00',
]) => {
  const numericYear = Number(year);
  const numericMonth = Number(month);
  const numericDay = Number(day);
  const numericHour = Number(hour);
  const numericMinute = Number(minute);
  const numericSecond = Number(second);
  const leapYear =
    numericYear % 4 === 0 &&
    (numericYear % 100 !== 0 || numericYear % 400 === 0);
  const daysInMonth = [
    31,
    leapYear ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];

  return (
    numericMonth >= 1 &&
    numericMonth <= 12 &&
    numericDay >= 1 &&
    numericDay <= daysInMonth[numericMonth - 1] &&
    numericHour <= 23 &&
    numericMinute <= 59 &&
    numericSecond <= 59
  );
};

const validIsoOffset = offset => {
  if (offset.toUpperCase() === 'Z') return true;

  const digits = offset.slice(1).replace(':', '');
  const hours = Number(digits.slice(0, 2));
  const minutes = Number(digits.slice(2));
  return hours <= 14 && minutes <= 59 && (hours < 14 || minutes === 0);
};

const parseReportingDateValue = (value, reportingTimezone) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const stringValue = String(value).trim();
  if (DATE_ONLY_PATTERN.test(stringValue)) {
    const date = zonedTimeToUtc(`${stringValue}T12:00:00`, reportingTimezone);
    const roundTrip = formatInTimeZone(date, reportingTimezone, 'yyyy-MM-dd');
    return roundTrip === stringValue ? date : null;
  }

  const localDateTime = stringValue.match(LOCAL_DATE_TIME_PATTERN);
  if (localDateTime) {
    const [, date, time, seconds = '00', fraction = ''] = localDateTime;
    const milliseconds = fraction.padEnd(3, '0');
    const wallClock = `${date}T${time}:${seconds}.${milliseconds}`;
    const converted = zonedTimeToUtc(wallClock, reportingTimezone);
    const roundTrip = formatInTimeZone(
      converted,
      reportingTimezone,
      'yyyy-MM-dd HH:mm:ss.SSS'
    );

    return roundTrip === `${date} ${time}:${seconds}.${milliseconds}`
      ? converted
      : null;
  }

  const offsetDateTime = stringValue.match(OFFSET_DATE_TIME_PATTERN);
  if (
    !offsetDateTime ||
    !validCalendarDateTimeParts(offsetDateTime) ||
    !validIsoOffset(offsetDateTime[8])
  ) {
    return null;
  }

  const instant = new Date(stringValue);
  return Number.isNaN(instant.getTime()) ? null : instant;
};

export const formatLocalizedDateInTimezone = (
  value,
  {
    locale = DEFAULT_LOCALE,
    timeZone = DEFAULT_TIMEZONE,
    dateStyle = 'short',
    timeStyle,
  } = {}
) => {
  if (missingDateValue(value)) return '';

  try {
    const reportingTimezone = normalizeReportingTimezone(timeZone);
    const date = parseReportingDateValue(value, reportingTimezone);
    if (!date) return '';

    return new Intl.DateTimeFormat(normalizeLocale(locale), {
      dateStyle,
      ...(timeStyle ? { timeStyle } : {}),
      timeZone: reportingTimezone,
    }).format(date);
  } catch {
    return '';
  }
};

const CSV_FORMULA_PREFIX = /^[\t\r\n ]*[=+\-@]/;

export const escapeCsvCell = value => {
  let text = value === null || value === undefined ? '' : String(value);
  if (CSV_FORMULA_PREFIX.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

export const sanitizeDownloadFilename = value =>
  String(value || 'export')
    .replace(/[<>:"/\\|?*]/g, '-')
    .replace(BIDI_CONTROL_CHARACTERS, '')
    .replace(/[\p{Cc}]/gu, '-')
    .replace(/\s+/g, ' ')
    .trim() || 'export';

export const zonedDateTimeToUtc = (
  date,
  time = '09:00',
  reportingTimezone = DEFAULT_TIMEZONE
) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(date || ''))) return null;
  if (!/^\d{2}:\d{2}$/.test(String(time || ''))) return null;

  try {
    const normalizedTimezone = normalizeReportingTimezone(reportingTimezone);
    const result = zonedTimeToUtc(`${date}T${time}:00`, normalizedTimezone);
    if (Number.isNaN(result.getTime())) return null;

    // date-fns-tz normalizes nonexistent wall-clock times during a DST jump.
    // Reject the conversion instead of scheduling at a different local time.
    const roundTrip = formatInTimeZone(
      result,
      normalizedTimezone,
      'yyyy-MM-dd HH:mm'
    );
    return roundTrip === `${date} ${time}` ? result : null;
  } catch {
    return null;
  }
};
